import React, { useEffect, useMemo, useRef, useState } from 'react';

let highlighterPromise;
function loadHighlighter() {
  if (!highlighterPromise) highlighterPromise = import('react-syntax-highlighter');
  return highlighterPromise;
}

function findScrollRoot(element) {
  let outermost = null;
  for (let node = element?.parentElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
    if (['auto', 'scroll'].includes(window.getComputedStyle(node).overflowY)) outermost = node;
  }
  return outermost;
}

// Highlighting may be unloaded outside the viewport, but its geometry must not
// change. Otherwise a tall block above the reader grows into the observer's
// margin, shrinks when highlighted, and repeats, moving the entire transcript.
export default function WebCodeBlock({ language, codeTheme, plainStyle, highlight, children }) {
  const [Highlighter, setHighlighter] = useState(null);
  const [nearViewport, setNearViewport] = useState(() => typeof IntersectionObserver === 'undefined');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!rootRef.current || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setNearViewport(!!entry?.isIntersecting), {
      root: findScrollRoot(rootRef.current), rootMargin: '1600px 0px', threshold: 0,
    });
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!highlight || !nearViewport) return undefined;
    let alive = true;
    loadHighlighter().then(module => {
      if (alive) setHighlighter(() => module.default);
    }).catch(() => {});
    return () => { alive = false; };
  }, [highlight, nearViewport]);

  // Use the web renderer's inline spans. The native adapter supplies its own
  // font size/line boxes and RN Text whitespace rules, unlike the placeholder.
  // Only colors vary by token, so even a wrapped long line has identical metrics.
  const colorTheme = useMemo(() => Object.fromEntries(Object.entries(codeTheme).map(([scope, style]) => [
    scope, style.color ? { color: style.color } : {},
  ])), [codeTheme]);
  const preStyle = {
    ...plainStyle,
    display: 'block',
    margin: 0,
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: `${plainStyle.lineHeight}px`,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'normal',
    tabSize: 4,
  };
  const codeStyle = { font: 'inherit', color: 'inherit', whiteSpace: 'inherit', padding: 0, background: 'transparent' };

  return <div ref={rootRef} style={{ minWidth: 0, maxWidth: '100%' }}>
    {Highlighter && highlight && nearViewport ? <Highlighter
      language={language} style={colorTheme} customStyle={preStyle}
      PreTag="pre" CodeTag="code" codeTagProps={{ style: codeStyle }} wrapLines={false}
    >{children}</Highlighter> : <pre style={preStyle}><code style={codeStyle}>{children}</code></pre>}
  </div>;
}
